import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Item } from '../../models/Item'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { Subscription } from '../../models/Subscription'
import { User } from '../../models/User'
import { API_BASE } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import {
    findSubscriptionFrequencySelection,
    subscriptionFrequencyOptions,
} from '../settings/Options'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    subscription: Subscription
    isWorkflowReviewStep: boolean
    client: User
    item: Item
    payments?: Payment[]
    packages?: Package[]
}

export const SubscriptionView = ({
    subscription,
    isWorkflowReviewStep,
    client,
    item,
    payments,
    packages,
}: Props) => {
    const navigate = useNavigate()

    const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false)
    const [isPackagesModalOpen, setIsPackagesModalOpen] = useState(false)

    const numRemainingPayments = Math.max(
        0,
        (subscription.instances ?? 0) - (subscription.paidInstances ?? 0)
    )

    const receipt = useMemo((): Receipt => {
        return {
            items: isWorkflowReviewStep
                ? undefined
                : [
                      {
                          id: '__item__',
                          title: item.name ?? '',
                          subtitle: item.rate === undefined ? 'TBD' : currencyFormat(item.rate),
                          quantity: 1,
                          price: item.rate ?? 0,
                          tertiaryView:
                              item.image?.uri !== undefined ? (
                                  <img src={item.image.uri} className="size-12 rounded-full" />
                              ) : (
                                  <Icon icon="tag.large" className="size-12" background="round" />
                              ),
                      },
                  ],
            modifiers: {
                subtotal: {
                    amount: subscription.total ?? 0,
                },
                setupFee:
                    subscription.deposit !== undefined && subscription.deposit > 0
                        ? {
                              amount: subscription.deposit,
                          }
                        : undefined,
                giftCertificate:
                    subscription.gift !== undefined
                        ? {
                              selectedGift: subscription.gift,
                          }
                        : undefined,
                discount:
                    subscription.discount !== undefined && subscription.discount > 0
                        ? {}
                        : undefined,
                salesTax:
                    subscription.taxcode !== undefined
                        ? {
                              amount:
                                  subscription.taxcode !== undefined
                                      ? (subscription.taxAmount ?? null)
                                      : null,
                              parenthetical:
                                  subscription.taxcode !== undefined
                                      ? `${subscription.taxcode.rate ?? 0}%`
                                      : null,
                          }
                        : undefined,
                surcharge: subscription.allowSurcharge
                    ? {
                          amount: subscription.allowSurcharge
                              ? (subscription.surchargeAmount ?? null)
                              : null,
                      }
                    : undefined,
            },
            total: subscription.chargeAmount,
        }
    }, [subscription])

    const frequencyDescription = (
        subscription.interval !== undefined && subscription.frequency !== undefined
            ? (findSubscriptionFrequencySelection(subscription.interval, subscription.frequency) ??
              subscriptionFrequencyOptions[0])
            : subscriptionFrequencyOptions[0]
    ).label

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        {item.iconConfig === undefined ? undefined : item.iconConfig.type ===
                          'image' ? (
                            <img src={item.iconConfig.uri} className="size-[116px] rounded-full" />
                        ) : (
                            <Icon
                                icon={item.iconConfig.icon}
                                background="round"
                                className="size-[116px]"
                                svgClassName="size-[56px]"
                            />
                        )}

                        <Heading size="sm" className="mt-6 mb-1 text-center">
                            {item?.name}
                        </Heading>

                        {!subscription.paidDate && (
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                Requires payment
                            </Paragraph>
                        )}

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {frequencyDescription}
                        </Paragraph>

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {currencyFormat(subscription.chargeAmount)}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!isWorkflowReviewStep &&
                            subscription.confirmedDate &&
                            subscription.nextInvoiceDate && (
                                <BaseRecordView.IconField
                                    icon="calendar.large"
                                    title={subscription.nextInvoiceDate.format('MMM D, YYYY')}
                                    subtitle="Next payment"
                                />
                            )}

                        {!!subscription.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={subscription.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={subscription.subaccount.name ?? ''}
                                subtitle={subscription.subaccount.labelString}
                                href={`/subaccounts/${subscription.subaccount.id}`}
                            />
                        )}

                        <BaseRecordView.IconField
                            icon={
                                <ContactAvatar
                                    user={client}
                                    hasHoverEffect={!isWorkflowReviewStep}
                                />
                            }
                            title={client.name ?? ''}
                            subtitle="Client"
                            secondaryView={
                                !isWorkflowReviewStep ? (
                                    <a href={`/messages/${client.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                ) : undefined
                            }
                            href={!isWorkflowReviewStep ? `/clients/${client.id}` : undefined}
                        />
                    </div>
                </Card>

                {!isWorkflowReviewStep && (
                    <Card size="sm" title="Status">
                        <RecordStatusChecklist
                            items={subscription.statuses.map((status) => ({
                                title: status.title,
                                subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                                status: status.date !== undefined ? 'checked' : 'empty',
                            }))}
                            className="mt-6"
                        />
                    </Card>
                )}
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                {payments !== undefined && payments.length > 0 && (
                    <Card className="flex flex-col items-stretch justify-start gap-y-8">
                        <div className="flex items-center justify-between">
                            <Heading size="sm" className="text-gray-700">
                                Payments{' '}
                                {payments.length > 0 && (
                                    <Label size="md" className="text-gray-600">
                                        ({payments.length})
                                    </Label>
                                )}
                            </Heading>

                            {payments.length > 5 && (
                                <Button
                                    intent="tertiaryLink"
                                    onClick={() => setIsPaymentsModalOpen(true)}
                                    className="px-0"
                                    size="small"
                                >
                                    See all
                                </Button>
                            )}
                        </div>

                        <div className="flex flex-col items-stretch justify-start gap-y-8">
                            {(subscription.instances ?? '') !== '' && (
                                <div className="flex flex-col items-stretch justify-center gap-y-1 rounded-lg border border-gray-300 px-6 py-4">
                                    <Heading size="sm" className="text-gray-700">
                                        {numRemainingPayments}
                                    </Heading>
                                    <Paragraph size="md" className="text-gray-600">
                                        {numRemainingPayments === 1
                                            ? 'Remaining payment'
                                            : 'Remaining payments'}
                                    </Paragraph>
                                </div>
                            )}

                            {payments.length > 0 && (
                                <div className="flex flex-col items-stretch justify-start gap-y-8">
                                    {payments.slice(0, 5).map((payment) => (
                                        <Link
                                            key={payment.id ?? ''}
                                            to={`/payments/${payment.id}`}
                                            className="group relative flex items-center justify-center gap-x-4"
                                        >
                                            <div className="absolute -inset-4 z-10 rounded-lg bg-black/4 opacity-0 group-hover:opacity-100" />

                                            {payment.client && (
                                                <ContactAvatar
                                                    user={payment.client}
                                                    className="z-20 shrink-0"
                                                />
                                            )}
                                            <div className="z-20 flex grow flex-col items-stretch justify-start gap-y-1">
                                                <div className="flex items-center justify-between">
                                                    <Label size="md" className="text-gray-700">
                                                        {payment.client?.name}
                                                    </Label>
                                                    <Label size="md" className="text-gray-700">
                                                        {currencyFormat(payment.amount)}
                                                    </Label>
                                                </div>
                                                <Paragraph size="sm" className="text-gray-600">
                                                    {payment.createdDate?.format('MMM Do, YYYY')}
                                                </Paragraph>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                <Card>
                    <Heading size="sm" className="mb-8 text-gray-700">
                        Payment details
                    </Heading>

                    <WorkflowPage.ReceiptView receipt={receipt} transaction={subscription} />
                </Card>

                {packages !== undefined && packages.length > 0 && (
                    <Card className="flex flex-col items-stretch justify-start gap-y-8">
                        <div className="flex items-center justify-between">
                            <Heading size="sm" className="text-gray-700">
                                Packages{' '}
                                {packages.length > 0 && (
                                    <Label size="md" className="text-gray-600">
                                        ({packages.length})
                                    </Label>
                                )}
                            </Heading>

                            {packages.length > 5 && (
                                <Button
                                    intent="tertiaryLink"
                                    onClick={() => setIsPackagesModalOpen(true)}
                                    className="px-0"
                                >
                                    See all
                                </Button>
                            )}
                        </div>

                        <div>
                            {packages.length > 0 && (
                                <div className="flex flex-col items-stretch justify-start gap-y-8">
                                    {packages.slice(0, 5).map((pkg) => (
                                        <Link
                                            key={pkg.id ?? ''}
                                            to={`/packages/${pkg.id}`}
                                            className="group relative flex items-center justify-center gap-x-4"
                                        >
                                            <div className="absolute -inset-4 z-10 rounded-lg bg-black/4 opacity-0 group-hover:opacity-100" />

                                            {pkg.item?.iconConfig === undefined ? undefined : pkg
                                                  .item.iconConfig.type === 'image' ? (
                                                <img
                                                    src={pkg.item.iconConfig.uri}
                                                    className="size-12 rounded-full"
                                                />
                                            ) : (
                                                <Icon
                                                    icon={pkg.item.iconConfig.icon}
                                                    background="round"
                                                    className="size-12"
                                                    svgClassName="size-6"
                                                />
                                            )}

                                            <div className="z-20 flex grow flex-col items-stretch justify-start gap-y-1">
                                                <Label size="md" className="text-gray-700">
                                                    {pkg.name}
                                                </Label>
                                                <Paragraph size="sm" className="text-gray-600">
                                                    {(pkg.expirationString ?? '')
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        (pkg.expirationString ?? '').slice(1)}
                                                </Paragraph>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {!isWorkflowReviewStep && (
                    <WorkflowPage.FormAndContractCard
                        form={subscription.form}
                        contract={subscription.contract}
                        onOpenForm={
                            subscription.record
                                ? () => navigate(`/forms/${subscription.record?.id}`)
                                : undefined
                        }
                        onOpenContract={
                            subscription.signature
                                ? () =>
                                      window.open(
                                          `${API_BASE}/subscription/${subscription.id}/contract`,
                                          '_blank'
                                      )
                                : undefined
                        }
                    />
                )}

                {(subscription.memo ?? '').trim().length > 0 && (
                    <Card>
                        <Heading size="sm" className="text-gray-700">
                            Memo
                        </Heading>

                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {subscription.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>

            {payments !== undefined && (
                <Modal
                    isOpen={isPaymentsModalOpen}
                    onClose={() => setIsPaymentsModalOpen(false)}
                    title={
                        <>
                            Payments{' '}
                            {payments.length > 0 && (
                                <Label size="md" className="text-gray-600">
                                    ({payments.length})
                                </Label>
                            )}
                        </>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-3">
                        {payments.map((payment) => (
                            <Link
                                key={payment.id ?? ''}
                                to={`/payments/${payment.id}`}
                                className="group relative flex items-center justify-center gap-x-4 rounded-lg border border-gray-300 p-4 hover:bg-black/4"
                            >
                                {payment.client && (
                                    <ContactAvatar
                                        user={payment.client}
                                        className="z-20 shrink-0"
                                    />
                                )}
                                <div className="z-20 flex grow flex-col items-stretch justify-start gap-y-1">
                                    <div className="flex items-center justify-between">
                                        <Label size="md" className="text-gray-700">
                                            {payment.client?.name}
                                        </Label>
                                        <Label size="md" className="text-gray-700">
                                            {currencyFormat(payment.amount)}
                                        </Label>
                                    </div>
                                    <Paragraph size="sm" className="text-gray-600">
                                        {payment.createdDate?.format('MMM Do, YYYY')}
                                    </Paragraph>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Modal>
            )}

            {packages !== undefined && (
                <Modal
                    isOpen={isPackagesModalOpen}
                    onClose={() => setIsPackagesModalOpen(false)}
                    title={
                        <>
                            Packages{' '}
                            {packages.length > 0 && (
                                <Label size="md" className="text-gray-600">
                                    ({packages.length})
                                </Label>
                            )}
                        </>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-3">
                        {packages.map((pkg) => (
                            <a
                                key={pkg.id ?? ''}
                                href={`/packages/${pkg.id}`}
                                className="group relative flex items-center justify-center gap-x-4 rounded-lg border border-gray-300 p-4 hover:bg-black/4"
                            >
                                {pkg.item?.iconConfig === undefined ? undefined : pkg.item
                                      .iconConfig.type === 'image' ? (
                                    <img
                                        src={pkg.item.iconConfig.uri}
                                        className="size-[116px] rounded-full"
                                    />
                                ) : (
                                    <Icon
                                        icon={pkg.item.iconConfig.icon}
                                        background="round"
                                        className="size-12"
                                        svgClassName="size-6"
                                    />
                                )}

                                <div className="z-20 flex grow flex-col items-stretch justify-start gap-y-1">
                                    <Label size="md" className="text-gray-700">
                                        {pkg.name}
                                    </Label>
                                    <Paragraph size="sm" className="text-gray-600">
                                        {(pkg.expirationString ?? '').charAt(0).toUpperCase() +
                                            (pkg.expirationString ?? '').slice(1)}
                                    </Paragraph>
                                </div>
                            </a>
                        ))}
                    </div>
                </Modal>
            )}
        </BaseRecordView.Container>
    )
}
